
import { createContext, useContext, useCallback, useState, type ReactNode } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { cn } from "@/lib/utils/cn";
import { IconCheck, IconAlertCircle, IconInfo, IconX } from "./Icons";
import type { ToastMessage } from "@/types";

interface ToastContextValue {
  toast: (message: Omit<ToastMessage, "id">) => void;
  success: (title: string, description?: string) => void;
  error: (title: string, description?: string) => void;
  warning: (title: string, description?: string) => void;
  info: (title: string, description?: string) => void;
  dismiss: (id: string) => void;
  dismissAll: () => void;
}

const ToastContext = createContext<ToastContextValue>({
  toast: () => {},
  success: () => {},
  error: () => {},
  warning: () => {},
  info: () => {},
  dismiss: () => {},
  dismissAll: () => {},
});

export function useToast() {
  return useContext(ToastContext);
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const dismissAll = useCallback(() => {
    setToasts([]);
  }, []);

  const toast = useCallback(
    (message: Omit<ToastMessage, "id">) => {
      const id = Math.random().toString(36).slice(2);
      const newToast: ToastMessage = { id, duration: 5000, ...message };

      setToasts((prev) => [newToast, ...prev].slice(0, 5));

      if (newToast.duration && newToast.duration > 0) {
        setTimeout(() => dismiss(id), newToast.duration);
      }
    },
    [dismiss]
  );

  const success = useCallback(
    (title: string, description?: string) => {
      toast({ type: "success", title, description });
    },
    [toast]
  );

  const error = useCallback(
    (title: string, description?: string) => {
      toast({ type: "error", title, description, duration: 8000 });
    },
    [toast]
  );

  const warning = useCallback(
    (title: string, description?: string) => {
      toast({ type: "warning", title, description });
    },
    [toast]
  );

  const info = useCallback(
    (title: string, description?: string) => {
      toast({ type: "info", title, description });
    },
    [toast]
  );

  return (
    <ToastContext.Provider value={{ toast, success, error, warning, info, dismiss, dismissAll }}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

function ToastViewport({
  toasts,
  onDismiss,
}: {
  toasts: ToastMessage[];
  onDismiss: (id: string) => void;
}) {
  return (
    <div
      className="fixed top-4 right-4 z-toast flex flex-col gap-2 w-[360px] max-w-[calc(100vw-2rem)]"
      aria-live="polite"
      aria-label="Notifications"
    >
      <AnimatePresence mode="popLayout">
        {toasts.map((toast) => (
          <ToastItem key={toast.id} toast={toast} onDismiss={onDismiss} />
        ))}
      </AnimatePresence>
    </div>
  );
}

const typeConfig = {
  success: {
    icon: IconCheck,
    bg: "bg-success/10 border-success/20",
    iconBg: "bg-success/15 text-success",
    titleColor: "text-foreground",
  },
  error: {
    icon: IconAlertCircle,
    bg: "bg-destructive/10 border-destructive/20",
    iconBg: "bg-destructive/15 text-destructive",
    titleColor: "text-foreground",
  },
  warning: {
    icon: IconAlertCircle,
    bg: "bg-warning/10 border-warning/20",
    iconBg: "bg-warning/15 text-warning",
    titleColor: "text-foreground",
  },
  info: {
    icon: IconInfo,
    bg: "bg-brand/10 border-brand/20",
    iconBg: "bg-brand/15 text-brand",
    titleColor: "text-foreground",
  },
};

function ToastItem({
  toast,
  onDismiss,
}: {
  toast: ToastMessage;
  onDismiss: (id: string) => void;
}) {
  const config = typeConfig[toast.type];
  const Icon = config.icon;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, x: 80, scale: 0.9 }}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      exit={{ opacity: 0, x: 80, scale: 0.9 }}
      transition={{ type: "spring", damping: 25, stiffness: 300 }}
      className={cn(
        "flex items-start gap-3 p-3.5 rounded-2xl border",
        "bg-surface-elevated backdrop-blur-sm shadow-xl",
        config.bg
      )}
      role="alert"
    >
      <div className={cn("w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0", config.iconBg)}>
        <Icon className="w-4 h-4" />
      </div>

      <div className="flex-1 min-w-0 pt-0.5">
        <p className={cn("text-sm font-semibold leading-snug", config.titleColor)}>
          {toast.title}
        </p>
        {toast.description && (
          <p className="text-xs text-text-muted mt-0.5 leading-relaxed">
            {toast.description}
          </p>
        )}
        {toast.action && (
          <button
            onClick={toast.action.onClick}
            className="mt-1.5 text-xs font-medium text-brand hover:text-brand-hover transition-colors"
          >
            {toast.action.label}
          </button>
        )}
      </div>

      <button
        onClick={() => onDismiss(toast.id)}
        className="w-6 h-6 flex items-center justify-center rounded-lg text-text-subtle hover:text-foreground hover:bg-muted transition-colors flex-shrink-0"
        aria-label="Dismiss notification"
      >
        <IconX className="w-3.5 h-3.5" />
      </button>
    </motion.div>
  );
}
